/**
 * Billing and Subscription Models
 * TypeScript interfaces matching backend billing schemas
 */

// Enums
export enum SubscriptionStatus {
  ACTIVE = 'active',
  TRIALING = 'trialing',
  PAST_DUE = 'past_due',
  CANCELED = 'canceled',
  UNPAID = 'unpaid',
  INCOMPLETE = 'incomplete',
  INCOMPLETE_EXPIRED = 'incomplete_expired',
  PAUSED = 'paused'
}

export enum PlanTier {
  FREE = 'free',
  STARTER = 'starter',
  PROFESSIONAL = 'professional',
  ENTERPRISE = 'enterprise'
}

export enum BillingInterval {
  MONTH = 'month',
  YEAR = 'year'
}

// Pricing Plan
export interface PricingPlan {
  tier: PlanTier;
  name: string;
  description: string;
  price_monthly: number;
  price_yearly: number;
  stripe_price_id_monthly?: string;
  stripe_price_id_yearly?: string;
  stripe_product_id?: string;
  monthly_api_calls_limit: number;
  monthly_documents_limit: number;
  max_users: number;
  max_templates: number;
  max_webhooks: number;
  features: string[];
  support_level: string;
  storage_gb: number;
}

// Subscription
export interface Subscription {
  _id: string;
  organization_id: string;
  stripe_customer_id?: string;
  stripe_subscription_id?: string;
  stripe_price_id?: string;
  plan_tier: PlanTier;
  billing_interval: BillingInterval;
  status: SubscriptionStatus;
  current_period_start?: Date;
  current_period_end?: Date;
  cancel_at_period_end: boolean;
  canceled_at?: Date;
  trial_start?: Date;
  trial_end?: Date;
  monthly_api_calls_used: number;
  monthly_documents_used: number;
  metadata?: Record<string, any>;
  created_at: Date;
  updated_at: Date;
}

// Usage Response
export interface UsageResponse {
  organization_id: string;
  plan_tier: string;
  billing_interval: string;
  current_period_start?: Date;
  current_period_end?: Date;
  
  // API calls
  monthly_api_calls_limit: number;
  monthly_api_calls_used: number;
  api_calls_remaining: number;
  api_calls_percentage: number;
  
  // Documents
  monthly_documents_limit: number;
  monthly_documents_used: number;
  documents_remaining: number;
  documents_percentage: number;
  
  // Other limits
  max_users: number;
  current_users: number;
  max_templates: number;
  current_templates: number;
  max_webhooks: number;
  current_webhooks: number;
  max_api_keys: number;
  current_api_keys: number;
  
  // Storage
  storage_gb_limit: number;
  storage_gb_used: number;
  storage_percentage: number;
}

// Request Schemas
export interface CreateSubscriptionRequest {
  stripe_price_id: string;
  payment_method_id?: string;
  trial_days?: number;
}

export interface UpdateSubscriptionRequest {
  stripe_price_id?: string;
  cancel_at_period_end?: boolean;
}

export interface CreateCheckoutSessionRequest {
  stripe_price_id: string;
  success_url: string;
  cancel_url: string;
  trial_days?: number;
}

// Response Schemas
export interface CheckoutSessionResponse {
  session_id: string;
  url: string;
}

export interface CustomerPortalResponse {
  url: string;
}

export interface ListPricingPlansResponse {
  plans: PricingPlan[];
}

export interface SubscriptionCreatedResponse {
  message: string;
  subscription: Subscription;
  requires_action: boolean;
  client_secret?: string;
}

export interface CancelSubscriptionResponse {
  message: string;
  subscription: Subscription;
  canceled_at: Date;
  cancel_at_period_end: boolean;
}

// Plan Metadata for UI Display
export interface PlanMetadata {
  tier: PlanTier;
  name: string;
  color: string;
  icon: string;
  popular?: boolean;
}

export const PLAN_METADATA: Record<PlanTier, PlanMetadata> = {
  [PlanTier.FREE]: {
    tier: PlanTier.FREE,
    name: 'Free',
    color: '#95a5a6',
    icon: 'rocket_launch'
  },
  [PlanTier.STARTER]: {
    tier: PlanTier.STARTER,
    name: 'Starter',
    color: '#27ae60',
    icon: 'trending_up'
  },
  [PlanTier.PROFESSIONAL]: {
    tier: PlanTier.PROFESSIONAL,
    name: 'Professional',
    color: '#0066b3',
    icon: 'business_center',
    popular: true
  },
  [PlanTier.ENTERPRISE]: {
    tier: PlanTier.ENTERPRISE,
    name: 'Enterprise',
    color: '#9b59b6',
    icon: 'corporate_fare'
  }
};

export const STATUS_METADATA: Record<SubscriptionStatus, { label: string; color: string; icon: string }> = {
  [SubscriptionStatus.ACTIVE]: {
    label: 'Active',
    color: '#27ae60',
    icon: 'check_circle'
  },
  [SubscriptionStatus.TRIALING]: {
    label: 'Trial',
    color: '#3498db',
    icon: 'schedule'
  },
  [SubscriptionStatus.PAST_DUE]: {
    label: 'Past Due',
    color: '#f39c12',
    icon: 'warning'
  },
  [SubscriptionStatus.CANCELED]: {
    label: 'Canceled',
    color: '#e74c3c',
    icon: 'cancel'
  },
  [SubscriptionStatus.UNPAID]: {
    label: 'Unpaid',
    color: '#e74c3c',
    icon: 'error'
  },
  [SubscriptionStatus.INCOMPLETE]: {
    label: 'Incomplete',
    color: '#f39c12',
    icon: 'pending'
  },
  [SubscriptionStatus.INCOMPLETE_EXPIRED]: {
    label: 'Expired',
    color: '#95a5a6',
    icon: 'event_busy'
  },
  [SubscriptionStatus.PAUSED]: {
    label: 'Paused',
    color: '#95a5a6',
    icon: 'pause_circle'
  }
};

// Helper Functions
export function getPlanMetadata(tier: PlanTier): PlanMetadata {
  return PLAN_METADATA[tier];
}

export function getStatusMetadata(status: SubscriptionStatus) {
  return STATUS_METADATA[status];
}

export function formatPrice(price: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(price);
}

export function calculateSavings(monthlyPrice: number, yearlyPrice: number): number {
  const yearlyEquivalent = monthlyPrice * 12;
  return Math.round(((yearlyEquivalent - yearlyPrice) / yearlyEquivalent) * 100);
}

